import React, { useEffect, useState, useContext } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import api from '../../services/api';
import moment from 'moment';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTheme } from '@material-ui/core';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Paleta de cores para diferenciar os usuários
const USER_COLORS = [
    '#3B71CA', '#F97316', '#10B981', '#EF4444', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F59E0B', '#6366F1',
    '#14B8A6', '#D946EF', '#0EA5E9', '#F43F5E', '#22C55E',
];

export const ChartsDate = ({ dateStart, dateEnd }) => {
    const theme = useTheme();
    const [ticketsData, setTicketsData] = useState({ data: [], count: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useContext(AuthContext);
    const companyId = user.companyId;
    const isDarkMode = theme.palette.type === 'dark';

    useEffect(() => {
        if (companyId && moment(dateStart).isValid() && moment(dateEnd).isValid()) {
            handleGetTicketsInformation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, dateStart, dateEnd]);

    const getColorWithOpacity = (color, opacity) => {
        if (color.startsWith('#')) {
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
        const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([.\d]+))?\)/);
        if (rgbaMatch) {
            return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
        }
        return color;
    };

    const handleGetTicketsInformation = async () => {
        try {
            setIsLoading(true);
            const { data } = await api.get(`/dashboard/ticketsDay?initialDate=${moment(dateStart).format('YYYY-MM-DD')}&finalDate=${moment(dateEnd).format('YYYY-MM-DD')}&companyId=${companyId}`);
            setTicketsData(data);
        } catch (error) {
            toast.error('Erro ao buscar informacoes dos tickets');
        } finally {
            setIsLoading(false);
        }
    };

    // Processar dados para uma unica barra por periodo (dia ou hora).
    // A quebra por atendente ja aparece em outras secoes do dashboard
    // (ranking, tabela de atendentes) - misturar as duas dimensoes aqui
    // deixava o grafico poluido, com uma barra por atendente por hora.
    const buildChartData = () => {
        const rawData = ticketsData?.data || [];

        if (rawData.length === 0) return { labels: [], datasets: [] };

        const getLabel = (item) => (item.hasOwnProperty('horario')
            ? `${String(item.horario).padStart(2, '0')}h`
            : item.data);

        const labelsUnicas = [...new Set(rawData.map(getLabel))];

        const totaisPorLabel = labelsUnicas.map(label =>
            rawData
                .filter(item => getLabel(item) === label)
                .reduce((soma, item) => soma + Number(item.total || 0), 0)
        );

        const dataset = {
            label: 'Atendimentos',
            data: totaisPorLabel,
            backgroundColor: getColorWithOpacity(USER_COLORS[0], 0.8),
            hoverBackgroundColor: USER_COLORS[0],
            borderWidth: 0,
            borderRadius: 4,
            barThickness: 28,
            maxBarThickness: 40,
        };

        return { labels: labelsUnicas, datasets: [dataset] };
    };

    const chartData = buildChartData();

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: chartData.datasets.length > 1,
                position: 'top',
                labels: {
                    color: isDarkMode ? '#e0e0e0' : '#424242',
                    usePointStyle: true,
                    padding: 16,
                },
            },
            tooltip: {
                backgroundColor: isDarkMode ? '#424242' : '#ffffff',
                titleColor: isDarkMode ? '#ffffff' : '#212121',
                bodyColor: isDarkMode ? '#e0e0e0' : '#757575',
                borderColor: isDarkMode ? '#616161' : '#e0e0e0',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 4,
                callbacks: {
                    title: (context) => context[0].label,
                    label: (context) => `${context.dataset.label}: ${context.raw}`,
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                    drawBorder: false,
                },
                ticks: {
                    color: isDarkMode ? '#bdbdbd' : '#757575',
                    maxRotation: 30,
                    minRotation: 30,
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    drawBorder: false,
                },
                ticks: {
                    color: isDarkMode ? '#bdbdbd' : '#757575',
                    padding: 8,
                    stepSize: 1,
                },
            },
        },
    };

    const styles = {
        container: {
            padding: 16,
            borderRadius: 8,
            boxShadow: theme.shadows[isDarkMode ? 4 : 1],
            backgroundColor: theme.palette.background.paper,
        },
        countBadge: {
            marginLeft: 8,
            padding: '4px 8px',
            backgroundColor: getColorWithOpacity(theme.palette.primary.main, 0.15),
            borderRadius: 4,
            fontSize: '0.875rem',
            color: theme.palette.primary.main,
        },
        chartContainer: {
            height: 350,
            position: 'relative',
            marginTop: 8,
        },
        emptyState: {
            height: 350,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isDarkMode ? 'rgba(66, 66, 66, 0.3)' : 'rgba(245, 245, 245, 0.7)',
            borderRadius: 4,
        },
    };

    return (
        <Paper style={styles.container} elevation={isDarkMode ? 2 : 1}>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
                {i18n.t("dashboard.users.totalAttendances")}
                <span style={styles.countBadge}>
                    {ticketsData?.count || 0}
                </span>
            </Typography>

            {chartData.labels.length === 0 ? (
                <div style={styles.emptyState}>
                    <Typography color="textSecondary">Nenhum dado disponivel para o periodo selecionado</Typography>
                </div>
            ) : (
                <div style={styles.chartContainer}>
                    <Bar options={options} data={chartData} />
                </div>
            )}
        </Paper>
    );
};

export default ChartsDate;
